import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, PlayCircle, DollarSign, Activity, Plus } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { useAllInstances, useActiveInstances } from '../hooks/useInstances';
import StatCard from '../components/StatCard';
import ProjectCard from '../components/ProjectCard';
import CreateProjectModal from '../components/CreateProjectModal';

function formatCost(n: number | string): string {
  const v = Number(n);
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function Dashboard() {
  const { data: projects, isLoading } = useProjects();
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const { data: allInstances = [] } = useAllInstances();
  const { data: activeInstances = [] } = useActiveInstances();

  const totalProjects = projects?.length ?? 0;
  const todayCost = allInstances
    .filter((i) => {
      const today = new Date().toDateString();
      return new Date(i.updatedAt).toDateString() === today;
    })
    .reduce((sum, i) => sum + Number(i.totalCostUsd), 0);

  const recentInstances = [...allInstances].slice(0, 5);

  const statusColor: Record<string, string> = {
    running: 'bg-green-500',
    starting: 'bg-blue-500',
    paused: 'bg-yellow-500',
    completed: 'bg-slate-400',
    failed: 'bg-red-500',
    stopped: 'bg-slate-400',
    pending: 'bg-slate-300',
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Overview of your projects and instances</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          New Project
        </button>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FolderKanban} label="Total Projects" value={totalProjects} />
        <StatCard icon={PlayCircle} label="Active Instances" value={activeInstances.length} />
        <StatCard icon={DollarSign} label="Cost Today" value={formatCost(todayCost)} />
        <StatCard icon={Activity} label="Total Instances" value={allInstances.length} />
      </div>

      {/* Active Instances */}
      {activeInstances.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Active Instances</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeInstances.map((inst) => (
              <button
                key={inst.id}
                onClick={() => navigate(`/instances/${inst.id}`)}
                className="rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300"
              >
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${statusColor[inst.status] ?? 'bg-slate-300'}`} />
                  <span className="text-sm font-medium text-slate-900">
                    {inst.agent?.name ?? 'Unknown agent'}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">
                  {inst.task?.title ?? 'Custom prompt'}
                </p>
                <div className="mt-2 flex gap-3 text-xs text-slate-400">
                  <span>{inst.model}</span>
                  <span>Turn {inst.currentTurn}/{inst.maxTurns}</span>
                  <span>${Number(inst.totalCostUsd).toFixed(2)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Instances */}
      {recentInstances.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Recent Instances</h2>
          <div className="rounded-xl border border-slate-200 bg-white">
            {recentInstances.map((inst, i) => (
              <button
                key={inst.id}
                onClick={() => navigate(`/instances/${inst.id}`)}
                className={`flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 ${
                  i < recentInstances.length - 1 ? 'border-b border-slate-100' : ''
                }`}
              >
                <div className={`h-2 w-2 shrink-0 rounded-full ${statusColor[inst.status] ?? 'bg-slate-300'}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-900">
                      {inst.agent?.name ?? 'Unknown agent'}
                    </span>
                    <span className="text-xs capitalize text-slate-400">{inst.status}</span>
                  </div>
                  <p className="truncate text-xs text-slate-500">
                    {inst.task?.title ?? 'Custom prompt'}
                  </p>
                </div>
                <div className="shrink-0 text-right text-xs text-slate-400">
                  <div>${Number(inst.totalCostUsd).toFixed(2)}</div>
                  <div>{relativeTime(inst.updatedAt)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Projects */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Recent Projects</h2>
        {isLoading ? (
          <div className="text-sm text-slate-400">Loading projects...</div>
        ) : !projects?.length ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
            <FolderKanban className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No projects yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 6).map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>

      <CreateProjectModal open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}
